import localforage from "localforage";
import { v4 as uuidv4 } from "uuid";
import * as api from "@app/api/inspection";
import { IN_PROGRESS, FOLLOW_UP, COMPLETED } from "@app/constants/Constants";
import Config from "@app/constants/Config";
import { format } from "date-fns";
import { OutdatedError } from "@app/lib/errors/OutdatedError";
import { getOffices } from "@app/models/office";
import { getSetting } from "@app/models/settings";
import { getFormerlyCompanyId } from "@app/utils/inspections";

const store = localforage.createInstance({
	name: "inspections",
});

async function buildInspection(data, type, status) {
	const [offices, officeId, inspections] = await Promise.all([getOffices(), getSetting("officeId")]);
	const office = offices.find(({ ID }) => ID.toString() === officeId);

	// TODO compute followupDate
	const followUpdate = data?.values?.followup_date;
	return {
		dispatcher: "dispatcherX", // TODO aus login
		officeId: parseInt(office?.ID, 10),
		officeName: office?.Branch,
		visitDate: format(new Date(), "yyyy-MM-dd HH:mm:ss"), // TODO wann setzen: am anfang, ende, mittendrin?
		followupInspection: null,
		...data,
		followupDate: followUpdate && format(followUpdate, "yyyy-MM-dd HH:mm:ss"),
		companyId: data?.values?.kundenundeinsatzort_kunde_kunde__ID,
		companyName: data?.values?.kundenundeinsatzort_kunde_kunde__NAME,
		type,
		status,
	};
}

const saveAsync = (uuid, newInspection) => async () => {
	let res;
	try {
		newInspection.meta.synced = true;
		res = await api.save(uuid, newInspection);
		await store.setItem(uuid, newInspection);
		return {
			success: true,
			response: res,
		};
	} catch (e) {
		console.error(`SYNC ERROR for '${uuid}'`, e);
		newInspection.meta.synced = false;
		await store.setItem(uuid, newInspection);
		return {
			success: false,
			error: e,
			response: res,
		};
	}
};

export async function createInspection(values, type) {
	const uuid = uuidv4();
	const newInspection = await buildInspection(
		{
			uuid,
			meta: {
				step: 1,
				synced: false,
			},
			values,
		},
		type,
		IN_PROGRESS,
	);

	await store.setItem(uuid, newInspection);
	return navigator.onLine ? { saveAsync: saveAsync(uuid, newInspection), uuid } : { uuid };
}

export async function updateInspection(uuid, values, rawStep, type, status = IN_PROGRESS) {
	const step = parseInt(rawStep, 10);
	const itemData = await store.getItem(uuid);
	const newInspectionData = await buildInspection(
		{
			...itemData,
			values: {
				...itemData.values,
				...values,
			},
			meta: {
				...itemData.meta,
				step,
				synced: "PENDING",
			},
		},
		type,
		status,
	);

	await store.setItem(uuid, newInspectionData);
	return navigator.onLine ? { saveAsync: saveAsync(uuid, newInspectionData), uuid } : { uuid };
}

export async function completeInspection(uuid, values, type) {
	console.log("completeInspection", { uuid, values, type });
	// TODO - json_data (Formular-Daten der Besichtigung)
	return updateInspection(uuid, values, "confirmation", type, COMPLETED);
}

export async function removeInspection(uuid) {
	const inspections = await getInspections();
	await api.remove(uuid);
	await store.removeItem(uuid);

	const parentInspection = inspections.find(({ followupInspection }) => followupInspection === uuid);
	if (parentInspection) {
		const updatedParent = await store.getItem(parentInspection.uuid);
		updatedParent.followupInspection = null;
		await saveAsync(updatedParent.uuid, updatedParent)();
	}
	return null;
}

// TODO
export async function syncInspection(uuid) {
	if (navigator.onLine) {
		const itemData = await store.getItem(uuid);
		const newData = {
			...itemData,
			meta: {
				...itemData.meta,
				synced: true,
			},
		};
		await api.save(uuid, newData);
		await store.setItem(uuid, newData);
		return null;
	} else {
		throw new Error(`Can not sync inspection ${uuid} since the application is still offline.`);
	}
}

export async function getInspection(uuid) {
	const rawInspection = await store.getItem(uuid);
	const { meta, values, type, status, followupDate, visitDate, __officeId, officeId, companyId, companyName } = rawInspection;
	return {
		...(meta ?? {}),
		uuid,
		values,
		type,
		status,
		followupDate,
		visitDate,
		officeId: officeId || __officeId,
		companyId,
		companyName,
	};
}

export async function createfollowupInspection(values, initialUUID) {
	// create FOLLOW-UP
	const newValues = { ...values };
	delete newValues.followup_date;
	delete newValues.confirmation_date;
	delete newValues.confirmation_name;
	const followupInspectionResult = await createInspection(newValues, FOLLOW_UP);

	// set reference from INITIAL to FOLLOW-UP
	const initialInspection = await store.getItem(initialUUID);
	const newInspectionData = {
		...initialInspection,
		followupInspection: followupInspectionResult.uuid,
	};
	if (navigator.onLine) {
		console.log("saveAsync");
		await saveAsync(initialUUID, newInspectionData)();
	} else {
		newInspectionData.meta.synced = false;
		await store.setItem(initialUUID, newInspectionData);
	}

	return followupInspectionResult;
}

export async function getInspectionsUUIds() {
	return store.keys();
}

export async function getInspections() {
	let cacheTime = await store.getItem("cacheTime");
	const valid = cacheTime && cacheTime + Config.API.FORM_CONFIG.CACHE > Date.now();

	if (!valid && navigator.onLine) {
		const inspectionsRes = await api.load();
		const inspections = Object.entries(inspectionsRes)
			.map(([__officeId, inspections]) => {
				return inspections.map((inspection) => ({
					...inspection,
					__officeId,
				}));
			})
			.flat();
		if (inspections?.length > 0) {
			await Promise.all(
				inspections?.map(async ({ uuid, branch, dispatcher, type, status, json_data, __officeId, ...form }) => {
					const existing = await store.getItem(uuid);
					const companyIdFormerly = getFormerlyCompanyId(form.company_id_formerly);
					const customerState = form.customer_state;
					// TODO Die Logik deckt nicht alle Fälle ab
					if (!existing || Object.keys(existing).length === 0) {
						await store.setItem(uuid, {
							uuid,
							branch,
							dispatcher,
							type,
							status,
							__officeId,
							visitDate: form.visit_date,
							followupDate: form.followup_date,
							followupInspection: form.followup_inspection,
							companyId: form.company_id,
							companyName: form.company_name,
							companyIdFormerly,
							customerState,
							...JSON.parse(json_data),
						});
					} else {
						let changed = false;
						if (companyIdFormerly && existing.companyIdFormerly === undefined) {
							existing.companyIdFormerly = companyIdFormerly;
							changed = true;
						}
						if (
							customerState !== undefined &&
							customerState !== "" &&
							existing.customerState === undefined
						) {
							existing.customerState = customerState;
							changed = true;
						}
						if (changed) {
							await store.setItem(uuid, existing);
						}
					}
				}),
			);
		}
		await store.setItem("cacheTime", Date.now());
	}
	if (!valid && !navigator.onLine) {
		throw new OutdatedError("inspection");
	}

	const keys = (await store.keys()).filter((key) => key !== "cacheTime");
	return await Promise.all(keys.map((key) => store.getItem(key)));
}

export async function clearInspections() {
	return store.clear();
}
